/**
 * integrationScope - what an organization lets a document-store integration
 * reach, and what it may do there.
 *
 * SharePoint and Google Drive connect with credentials the firm issues
 * (an Azure app registration with `Sites.Selected`; a Google service account
 * the firm shares folders with), so the PROVIDER already fences where the
 * credential can see. This scope is the firm's own, second statement of that
 * boundary — stored unencrypted in `OrganizationMcpConnector.metadata.scope`
 * so the Compliance page can show it back — and the workspace's MCP toolkits
 * enforce it on every call. Two fences, one of them ours, so a misgranted
 * site on the provider side still does not widen what eve reads.
 *
 * Shared by the domain API (writes it), the workspace server (enforces it)
 * and the web client (edits it). Pure: no node APIs, safe in the browser.
 *
 * @module integrationScope
 */

/**
 * What a firm-scoped toolkit may do, each gated separately. `upload` is
 * the one that writes into a document store: it is what "Open in SharePoint /
 * Google Drive" on a workspace file uses to place a copy there, and it needs
 * the credential itself to hold write access (Sites.Selected `write`; a
 * folder shared with the service account as editor). `write` is the record
 * system's equivalent — RuleGuard tools that create or amend a register
 * entry — and is never offered for a document store. Each integration shows
 * only the operations that mean something for it (the web's scope specs);
 * the default scope below is read-shaped for every one of them.
 */
export const DOCUMENT_STORE_OPERATIONS = [
  "search",
  "list",
  "read",
  "download",
  "upload",
  "write",
] as const;
export type DocumentStoreOperation = (typeof DOCUMENT_STORE_OPERATIONS)[number];

export interface DocumentStoreScope {
  /**
   * Where the integration may look. SharePoint: site URLs, optionally with a
   * library path (`https://firm.sharepoint.com/sites/Compliance/Shared
   * Documents/Policies`). Google Drive: folder ids or folder URLs. Laserfiche:
   * repository folder paths (`\\Compliance\\Policies`) or entry ids. RuleGuard:
   * the names of the tools eve may call. Empty means "everything the
   * credential itself can reach" — the provider's grant is then the only
   * fence.
   */
  readonly locations: ReadonlyArray<string>;
  /** The operations eve may perform there. Anything absent is refused. */
  readonly operations: ReadonlyArray<DocumentStoreOperation>;
}

/**
 * The scope an integration starts with when the firm has not said otherwise:
 * read-oriented, no raw downloads. Download is the one that moves whole
 * files out of the store, so it is opt-in.
 */
export const DEFAULT_DOCUMENT_STORE_SCOPE: DocumentStoreScope = {
  locations: [],
  operations: ["search", "list", "read"],
};

/** Bounds so a scope stays a short list a person can review, not a dump. */
export const MAX_SCOPE_LOCATIONS = 50;
const MAX_LOCATION_LENGTH = 512;

const isOperation = (value: unknown): value is DocumentStoreOperation =>
  typeof value === "string" &&
  (DOCUMENT_STORE_OPERATIONS as ReadonlyArray<string>).includes(value);

/**
 * Trim, de-duplicate and bound a scope as entered. Unknown operations are
 * dropped rather than failing the save: the form only offers known ones, so
 * an unknown value is a stale client, not a user mistake worth blocking on.
 */
export const normalizeIntegrationScope = (input: {
  readonly locations?: ReadonlyArray<unknown> | undefined;
  readonly operations?: ReadonlyArray<unknown> | undefined;
}): DocumentStoreScope => {
  const locations: string[] = [];
  for (const raw of input.locations ?? []) {
    if (typeof raw !== "string") continue;
    const trimmed = raw.trim().slice(0, MAX_LOCATION_LENGTH);
    if (trimmed.length === 0 || locations.includes(trimmed)) continue;
    locations.push(trimmed);
    if (locations.length >= MAX_SCOPE_LOCATIONS) break;
  }
  const operations = DOCUMENT_STORE_OPERATIONS.filter((operation) =>
    (input.operations ?? []).some((raw) => raw === operation),
  );
  return { locations, operations };
};

/**
 * The scope stored under `metadata.scope`, or null when the row carries none
 * (an integration configured before scopes existed, or one that never had
 * one). Callers decide what null means — the API reports it as unset, the
 * toolkits fall back to the default.
 */
export const parseIntegrationScope = (metadata: unknown): DocumentStoreScope | null => {
  if (typeof metadata !== "object" || metadata === null || Array.isArray(metadata)) return null;
  const raw = (metadata as Record<string, unknown>)["scope"];
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  return normalizeIntegrationScope({
    locations: Array.isArray(record["locations"]) ? record["locations"] : [],
    operations: Array.isArray(record["operations"])
      ? record["operations"].filter(isOperation)
      : [],
  });
};

/** The scope a toolkit enforces: what is stored, else the default. */
export const effectiveIntegrationScope = (metadata: unknown): DocumentStoreScope =>
  parseIntegrationScope(metadata) ?? DEFAULT_DOCUMENT_STORE_SCOPE;

/** Whether the scope permits an operation. */
export const scopeAllowsOperation = (
  scope: DocumentStoreScope,
  operation: DocumentStoreOperation,
): boolean => scope.operations.includes(operation);
